import calcAvgWithoutLeast from '@glass/common/modules/resumes/calcResumeScore/calcAvgWithoutLeast';
import countObjectValues from '@glass/common/modules/resumes/calcResumeScore/countObjectValues';
import scaleScore from '@glass/common/modules/resumes/calcResumeScore/scaleScore';
import { ResumeType, ResumeV2SectionType } from '@glass/common/modules/resumes/types';
import listInitialValue from '@glass/web/components/TextEditor/helpers/listInitialValue';
import { EDUCATION_SECTION, EXPERIENCE_SECTION } from '@glass/web/modules/resumes/router/sections';

const PERCENTILE_DETAILS_CHAR_COUNT = 200;
const MIN_DETAILS_LENGTH = listInitialValue.length + 1;

const EXPECTED_REGULAR_SECTION_COUNT = 1.6;

const countSectionValues = (obj: any) =>
  countObjectValues(obj, { ignoreKeys: ['id', 'index', 'hidden', 'type'] });

const isDetailsSection = ({ hidden, details }: ResumeV2SectionType) =>
  !hidden && details && details.length > MIN_DETAILS_LENGTH;

const mapDetailLength = (arr: Array<ResumeV2SectionType>) =>
  arr.map((section) => {
    if (isDetailsSection(section)) {
      return section.details?.length || 0;
    }
    return 0;
  });

const getArrayScore = (
  arr: Array<ResumeV2SectionType>,
  {
    percentileCount = 5,
    percentileLength = 0.8,
    countWeight = 1,
    lengthWeight = 1,
    detailsWeight = 1,
  },
) => {
  const countScore = scaleScore(countSectionValues({ arr }), 0, percentileCount);
  const lengthScore = scaleScore(arr.length, 0, percentileLength);
  const detailsLengthScore = scaleScore(
    calcAvgWithoutLeast(mapDetailLength(arr)),
    MIN_DETAILS_LENGTH,
    PERCENTILE_DETAILS_CHAR_COUNT,
  );

  return (
    (countWeight * countScore + lengthWeight * lengthScore + detailsWeight * detailsLengthScore) /
    (countWeight + lengthWeight + detailsWeight)
  );
};

const isVisibleSection = (section: ResumeV2SectionType) => section && !section.hidden;

const isRegularSection = (section: ResumeV2SectionType) =>
  section && ![EXPERIENCE_SECTION, EDUCATION_SECTION].includes(section.type || '');

const calRegularSectionScore = (regSections: Array<ResumeV2SectionType>) => {
  const regularCountScore = scaleScore(regSections.length, 0, EXPECTED_REGULAR_SECTION_COUNT);
  const regularLengthsScore = scaleScore(
    calcAvgWithoutLeast(mapDetailLength(regSections)),
    MIN_DETAILS_LENGTH,
    PERCENTILE_DETAILS_CHAR_COUNT,
  );
  return (regularCountScore + regularLengthsScore) / 2;
};

const calcResumeProgress = (resume: ResumeType) => {
  const { v2 } = resume || {};
  const { contact, sections = [], experiences = [], educations = [] } = v2 || {};

  const visibleSections = sections.filter(isVisibleSection);

  const contactValuesScore = scaleScore(countSectionValues(contact), 0, 6);
  const experienceValuesScore = getArrayScore(experiences, { percentileCount: 5 });
  const educationValuesScore = getArrayScore(educations, {
    percentileCount: 4,
    detailsWeight: 0.2,
  });

  const regularSections = visibleSections.filter(isRegularSection);
  const averageRegularSectionScore = calRegularSectionScore(regularSections);

  return Math.max(
    Math.min(
      scaleScore(
        contactValuesScore +
          experienceValuesScore +
          educationValuesScore +
          regularSections.length * averageRegularSectionScore,
        0,
        80 * (EXPECTED_REGULAR_SECTION_COUNT + 3),
      ),
      95,
    ),
    3,
  );
};

export default calcResumeProgress;
